import type { Role, sessionObject, sessionResponseObject } from "@minket/core";
import type { z } from "zod";

/**
 * `GET /api/session`: who is signed in and which organizations they belong to, or `{ user:
 * null }` when there is no session. The types are core's schemas, in their wire shape
 * (snake_case); the response is checked against the same schema in `sessionQuery`.
 */
export type Session = z.infer<typeof sessionResponseObject>;
export type SessionUser = z.infer<typeof sessionObject>["user"];
export type Membership = z.infer<typeof sessionObject>["memberships"][number];

export const roleLabels: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  developer: "Developer",
  viewer: "Viewer",
};

/** The name to show for the signed-in person: their name when they have one, else the address. */
export function displayName(user: SessionUser): string {
  return user.name ?? user.email;
}

/**
 * Whether this browser had a session last time (localStorage, set and cleared as sessions come
 * and go). The session cookie is HttpOnly, so this is the only early hint: with it, the shell's
 * skeleton shows while the session loads; without it (signed out, a new browser), a blank page
 * does, and nobody signed out sees a dashboard's shape before the sign-in page.
 */
const SIGNED_IN = "minket.signedIn";

export function hadSession(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN) === "1";
  } catch {
    return false;
  }
}

export function rememberSession(signedIn: boolean) {
  try {
    if (signedIn) window.localStorage.setItem(SIGNED_IN, "1");
    else window.localStorage.removeItem(SIGNED_IN);
  } catch {
    // Blocked storage: the blank page shows while the session loads.
  }
}
